import type { WebsiteDto } from "../../shared/types/websiteTypes";
import type { FC, Dispatch, SetStateAction } from "react";
import type { NotificationType } from "../../shared/types/notificationSettingsTypes";
import type { NotificationsPreviewsStringsProps } from "../types";

import { Box, Typography } from "@mui/material";
import NotificationPreview from "../NotificationPreview";

import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getUserInfo } from "../../services/selectors/user-selectors";
import { notificationTypes } from "../../constants/notifications";
import { isUSA } from "../../constants/env";

interface NotificationTypeSelectorProps {
  websiteDto?: WebsiteDto;
  notificationsPreviewsStrings: NotificationsPreviewsStringsProps;
  setApiKeyModalOpenFor: Dispatch<
    SetStateAction<
      | "zbozi"
      | "heureka"
      | "spolehlivaRecenze"
      | "googleReviews"
      | "trustpilotReviews"
      | undefined
    >
  >;
  createNotification: (type: NotificationType) => Promise<void>;
}

export const NotificationTypeSelector: FC<NotificationTypeSelectorProps> = ({
  websiteDto,
  notificationsPreviewsStrings,
  createNotification,
  setApiKeyModalOpenFor,
}) => {
  const { t } = useTranslation();
  const user = useSelector(getUserInfo);
  const theme = useTheme();

  const handleNotificationClick = (type: NotificationType) => {
    if (type === "heureka" && !websiteDto?.heurekaApiKey) {
      setApiKeyModalOpenFor("heureka");
      return;
    }
    if (type === "zboziCompanyRating" && !websiteDto?.zboziApiKey) {
      setApiKeyModalOpenFor("zbozi");
      return;
    }
    if (
      type === "spolehlivaRecenzeShopRating" &&
      !websiteDto?.spolehlivaRecenzeShopId
    ) {
      setApiKeyModalOpenFor("spolehlivaRecenze");
      return;
    }
    if (type === "googleReviews" && !websiteDto?.googleReviewsPlaceId) {
      setApiKeyModalOpenFor("googleReviews");
      return;
    }
    createNotification(type);
  };

  return (
    <Box
      sx={{ display: "flex", flexWrap: "wrap", gap: 3, marginLeft: 3, pb: 3 }}
    >
      {notificationTypes.map(({ type, name, hint, onlyUsaVisible }) => {
        // General permission check - must be admin or registered from specific platforms
        const isAllowed =
          user.isAdmin ||
          ["internal", "shoptet", "webareal", "upgates", "shopify", "eshop-rychle"].includes(
            user.registeredFrom,
          );
        if (!isAllowed) return null;

        // Region-specific filters (same logic as ApiKeyRow)
        if (onlyUsaVisible !== undefined) {
          if (onlyUsaVisible && !isUSA) {
            return null; // Notification only for USA, but user is not in USA
          }
          if (!onlyUsaVisible && isUSA) {
            return null; // Notification not for USA, but user is in USA
          }
        }

        // Platform-specific filters
        if (type === "shoptetLastOrders" || type === "shoptetCouponCode") {
          const isShoptetUser = user.isAdmin || user.registeredFrom === "shoptet";
          if (!isShoptetUser) return null;
        }

        if (type === "shopifyLastOrders") {
          const isShopifyUser = user.isAdmin || user.registeredFrom === "shopify";
          if (!isShopifyUser) return null;
        }

        if (type === "eshopRychleLastOrders") {
          const isEshopRychleUser = user.isAdmin || user.registeredFrom === "eshop-rychle";
          if (!isEshopRychleUser) return null;
        }

        return (
          <Box
            key={type}
            sx={{
              width: 390,
              border: "1px solid",
              borderColor: "grey.400",
              borderRadius: 2,
              padding: 2,
              cursor: "pointer",
              "&:hover": { backgroundColor: "grey.100" },
            }}
            onClick={() => handleNotificationClick(type)}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                mb: 1,
                color: theme.palette.main.secondary.u1,
              }}
            >
              {t(name)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
              {t(hint)}
            </Typography>
            <NotificationPreview
              height={130}
              backgroundColor='transparent'
              notificationHtml={notificationsPreviewsStrings[type]}
            />
          </Box>
        );
      })}
    </Box>
  );
};
